import { RadioGroup } from "radix-ui";

import { cn } from "./cn";

export interface Choice<T extends string> {
  value: T;
  label: string;
  detail: string;
}

/**
 * A few options with a line of explanation each, as large tappable cards (a radio group underneath).
 * `value` null = nothing chosen yet (for decisions that shouldn't have a default).
 */
export function ChoiceCards<T extends string>({
  label,
  choices,
  value,
  onChange,
}: {
  label: string;
  choices: readonly Choice<T>[];
  value: T | null;
  onChange: (value: T) => void;
}) {
  return (
    <fieldset className="flex flex-col gap-1.5">
      <legend className="mb-1.5 text-sm font-bold text-ink-2">{label}</legend>
      <RadioGroup.Root
        value={value ?? ""}
        onValueChange={(next) => onChange(next as T)}
        className="grid grid-cols-1 gap-2 sm:grid-cols-2"
      >
        {choices.map((choice) => (
          <RadioGroup.Item
            key={choice.value}
            value={choice.value}
            className={cn(
              "group flex items-start gap-3 rounded-xl border border-border-strong bg-surface p-3.5 text-left transition-colors",
              "hover:bg-surface-muted data-[state=checked]:border-2 data-[state=checked]:border-primary data-[state=checked]:bg-primary-soft data-[state=checked]:p-[13px]",
            )}
          >
            <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border-2 border-border-strong group-data-[state=checked]:border-primary">
              <RadioGroup.Indicator className="size-2.5 rounded-full bg-primary" />
            </span>
            <span className="flex flex-col gap-0.5">
              <span className="text-sm font-bold text-ink">{choice.label}</span>
              <span className="text-[13px] text-muted">{choice.detail}</span>
            </span>
          </RadioGroup.Item>
        ))}
      </RadioGroup.Root>
    </fieldset>
  );
}
